import * as React from 'react';

import * as R from 'ramda';
import debounce from 'debounce-promise';
import { connect } from 'react-redux';

import { ThunkDispatch } from '@src/domain';
import { checkPasswordStrength } from '@src/domain/User/UserActions';
import Text from '@src/components/legos/primitives/Text';
import { passwordStrengthLabels } from '@src/domain/Ui/UiConstants';
import Box from '@src/components/legos/primitives/Box';
import LoadingSpinner from '@src/components/legos/LoadingSpinner';

interface IPasswordStrengthProps {
  password: string;
  error: boolean | undefined;
}

const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  checkPasswordStrength: (password: string) => dispatch(checkPasswordStrength(password)),
});

type PasswordStrengthProps = ReturnType<typeof mapDispatchToProps> & IPasswordStrengthProps;

const getColor = (score: number) => {
  if (score < 2) {
    return 'state.danger';
  }
  if (score < 3) {
    return 'state.accent';
  }
  return 'state.success';
};

const PasswordStrengthCheckerFC: React.FC<PasswordStrengthProps> = ({ password, checkPasswordStrength, error }) => {
  const [score, setScore] = React.useState(null as number | null);

  const debouncedCheckStrength = React.useCallback(debounce(checkPasswordStrength, 250), []);

  React.useEffect(() => {
    setScore(null);
    debouncedCheckStrength(password)
      .then(({ data: { score } }) => {
        setScore(score);
      });
  }, [password, debouncedCheckStrength]);

  if (password.length < 6 || error) {
    return null;
  }

  const content = R.isNil(score)
    ? <LoadingSpinner />
    : (<Text color={(getColor(score))}>{passwordStrengthLabels[score]}</Text>);

  return (
    <Box style={{ position: 'absolute', top: -23, right: 0 }}>
      {content}
    </Box>
  );
};

export default connect(null, mapDispatchToProps)(PasswordStrengthCheckerFC);
